import React from 'react';
import BottomSheet from '../BottomSheet';
import { RARITY, CATEGORY_FLAVOR } from '../../lib/badgeDefinitions';
import { formatBadgeDate } from './badgeHelpers';

/**
 * Immersive badge detail bottom sheet.
 * z-[1200] così sta sopra "Vedi tutto" (z-[1100]) e la BottomNav.
 * readOnly: per i badge di ALTRI utenti (profilo pubblico) — solo nome,
 * descrizione, criterio e rarità; niente "Imposta come titolo" né condivisione.
 */
function BadgeDetailSheet({ badge, earned, earnedAt, selected, progress, onClose, onSetTitle, readOnly = false }) {
  if (!badge) return null;

  const rarity = RARITY[badge.rarity] || RARITY.comune;
  const masked = badge.secret && !earned;
  // Progresso su tutti i badge bloccati; per i segreti niente numero/target.
  const showProgress = !earned && progress && progress.target > 0;
  const pct = showProgress ? Math.min((progress.current / progress.target) * 100, 100) : 0;

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const handleShare = () => {
    if (!canShare) return;
    navigator
      .share({
        title: `Uptra — ${badge.name}`,
        text: `Ho sbloccato il badge "${badge.name}" su Uptra! ${badge.icon}`,
      })
      .catch(() => {});
  };

  const footer = readOnly ? null : earned ? (
    <>
      <button
        type="button"
        onClick={() => onSetTitle(badge.key)}
        className={`w-full py-3.5 rounded-2xl font-semibold text-base transition-colors ${
          selected
            ? 'bg-primary-50 text-primary-700 border-2 border-primary-500'
            : 'bg-primary-600 text-white'
        }`}
      >
        {selected ? '✓ Titolo attivo — rimuovi' : 'Imposta come titolo'}
      </button>
      {canShare && (
        <button
          type="button"
          onClick={handleShare}
          className="w-full py-3 rounded-2xl font-semibold text-sm bg-white text-gray-600 border border-gray-200 active:bg-gray-50"
        >
          Condividi
        </button>
      )}
    </>
  ) : (
    <button
      type="button"
      disabled
      className="w-full py-3.5 rounded-2xl font-semibold text-base bg-gray-100 text-gray-400"
    >
      🔒 Bloccato — completa la sfida
    </button>
  );

  return (
    <BottomSheet onClose={onClose} zIndexClass="z-[1200]" closeButton footer={footer}>
      <div className="px-6 pt-1 pb-4">
        {/* Emblem */}
        <div className="flex flex-col items-center text-center">
          <div
            className={`w-28 h-28 rounded-3xl flex items-center justify-center ring-1 bg-gradient-to-b ${
              earned ? `${rarity.glow} ${rarity.ring}` : 'from-gray-100 to-gray-50 ring-gray-200'
            }`}
          >
            <span className={`text-6xl ${earned ? '' : 'grayscale opacity-40'}`}>
              {masked ? '❓' : badge.icon}
            </span>
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">{masked ? '???' : badge.name}</h2>
          <div className="mt-2 flex items-center gap-2">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${rarity.chip}`}>
              {rarity.label}
            </span>
            <span
              className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                earned ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {earned ? 'Sbloccato' : 'Bloccato'}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="mt-6 space-y-4">
          <Field label="Descrizione">
            {masked ? 'Badge segreto. Continua ad allenarti per svelarlo.' : CATEGORY_FLAVOR[badge.category] || ''}
          </Field>

          <Field label={earned ? 'Sfida completata' : 'Come sbloccarlo'}>
            {masked ? 'Obiettivo nascosto 🤫' : badge.description}
          </Field>

          {showProgress && (
            <div>
              <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                <span className="font-bold uppercase tracking-wide">Progresso</span>
                {!masked && (
                  <span className="font-semibold text-gray-500">
                    {Math.min(progress.current, progress.target)}/{progress.target}
                  </span>
                )}
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all"
                  style={{ width: `${pct.toFixed(1)}%` }}
                />
              </div>
            </div>
          )}

          {earned && earnedAt && (
            <Field label="Sbloccato il">{formatBadgeDate(earnedAt, true)}</Field>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">{label}</p>
      <p className="text-sm text-gray-700">{children}</p>
    </div>
  );
}

export default BadgeDetailSheet;
